import React from 'react';
import { Drawer, Box, IconButton } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Theme } from '@orijinworks/frontend-commons';
import { FeedAnnouncement } from './feed-announcement';
import { FeedAuditConversations } from './feed-audit-conversations';
import { drawerStyles, drawerContentStyles } from './FeedDrawer.styles';

/**
 * Reusable drawer component for displaying feed content
 * Currently supports 'announcement' and 'audit-conversations' drawerType, extensible for other content types
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls drawer visibility
 * @param {Function} props.onClose - Handler for closing the drawer
 * @param {string} props.drawerType - Type of content to render (e.g., 'announcement', 'audit-conversations')
 * @param {Object} props.announcementData - Announcement data object to display (required when drawerType is 'announcement')
 * @param {Object} props.conversationData - Conversation data object containing messageId (required when drawerType is 'audit-conversations')
 * @param {Function} [props.onAnnouncementDeleteHandler] - Optional callback for announcement deletion
 * @returns {JSX.Element} The FeedDrawer component
 */
const FeedDrawer = React.memo(({
  open,
  onClose,
  drawerType,
  announcementData,
  conversationData,
  onAnnouncementDeleteHandler
}) => {
  const renderContent = () => {
    switch (drawerType) {
      case 'announcement':
        return (
          <FeedAnnouncement
            announcementData={announcementData}
            onAnnouncementDeleteHandler={onAnnouncementDeleteHandler}
          />
        );
      case 'audit-conversations':
        return (
          <FeedAuditConversations
            conversationData={conversationData}
          />
        );
      default:
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            padding="20px"
            role="status"
            aria-label="Unknown drawer type"
          >
            Unknown drawer type: {drawerType}
          </Box>
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: drawerStyles,
      }}
    >
      <Theme>
        <Box sx={drawerContentStyles}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 1,
              color: '#0F48B8'
            }}
            aria-label="Close drawer"
          >
            <KeyboardDoubleArrowRightIcon />
          </IconButton>
          <Box height="95vh">
            {renderContent()}
          </Box>
        </Box>
      </Theme>
    </Drawer>
  );
});

FeedDrawer.displayName = 'FeedDrawer';

export default FeedDrawer;

